import { AxiosResponse } from "axios";
import api from "../Interceptors/Interceptor";
import ApiResponse from "../Models/ApiResponse";
import { Page } from "../Models/Page";
import ReferenceModel from "../Models/ReferenceModel";
import { INotificationRecipient } from "../Models/NotificationRecipientModel";
import { BaseService } from "./BaseService";
import {
    AlertEvaluationScopeMode,
    AlertScopeResolveDurationUnit,
    AlertScopeResolveMode,
    EvaluationPeriodValue,
} from "../Pages/Alert/alertConstants";
import { IProcessflowAutomaticCriteriaCondition } from "./ProcessflowService";
import { normalizeAlertEvent } from "../Pages/Alert/utils/alertEventNormalizer";

export interface IAlertCriteriaCondition {
    parameter?: string;
    sqlOperator?: string;
    values?: string[];
    value?: string;
    dataType?: string;
    valueKind?: string;
}

export interface IAlertEvaluationScope {
    uuid?: string;
    parameter?: string;
    name?: string;
    values?: string[];
    label?: string;
    sequence?: number;
    incidentResolved?: boolean;
    permanentResolve?: boolean;
    permanentlyResolved?: boolean;
    /** Set while the scope is temporarily resolved: runs skip it until this moment. */
    resolvedUntil?: string;
}

export interface IAlertScopeResolvePayload {
    mode: AlertScopeResolveMode;
    /** Only for a temporary resolve. */
    durationAmount?: number;
    durationUnit?: AlertScopeResolveDurationUnit;
}

export interface IAlertEvaluationScopePreview {
    uuid?: string;
    label?: string;
    value?: number;
    sampleSize?: number;
    permanentlyResolved?: boolean;
    values?: string[];
}

export interface IAlertCriteriaManualScopeInput {
    evaluationScopeUuid: string;
    value: number;
}

export interface IAlertCriteriaManualInputPayload {
    value?: number;
    note?: string;
    evaluationScopeUuid?: string;
    scopes?: IAlertCriteriaManualScopeInput[];
}

export interface IAlertEvaluationCriteria {
    uuid?: string;
    metricView?: ReferenceModel;
    metricDatasetName?: string;
    metricTableName?: string;
    currentValue?: number;
    nextRunTs?: string;
    lastRunTs?: string;
    latestResultExecutedAt?: string;
    latestResultStatus?: string;
    latestResultContext?: string;
    datapoint?: string;
    functionName?: string;
    datapointExpression?: string;
    datapointBuilderStateJson?: string;
    datapointConfigurationMode?: string;
    evaluationPeriod?: ReferenceModel;
    evaluationPeriodValue?: EvaluationPeriodValue;
    evaluationFrequency?: ReferenceModel;
    evaluationScopeMode?: AlertEvaluationScopeMode;
    evaluationScopes?: IAlertEvaluationScope[];
    conditions?: IProcessflowAutomaticCriteriaCondition[];
}

export interface IAlertModel {
    uuid: string;
    customer?: ReferenceModel;
    title: string;
    requiredPermission?: string;
    roles?: ReferenceModel[];
    type?: ReferenceModel;
    severity?: ReferenceModel;
    status?: ReferenceModel;
    metricView?: ReferenceModel;
    dashboard?: ReferenceModel;
    datapoint?: string;
    functionName?: string;
    datapointExpression?: string;
    datapointBuilderStateJson?: string;
    datapointConfigurationMode?: string;
    runFrequency?: ReferenceModel;
    evaluationStartOn?: string;
    evaluationPeriod?: ReferenceModel;
    evaluationPeriodValue?: EvaluationPeriodValue;
    evaluationScopeMode?: AlertEvaluationScopeMode;
    evaluationScopes?: IAlertEvaluationScope[];
    triggeredScopeCount?: number;
    totalScopeCount?: number;
    thresholdCondition?: ReferenceModel;
    thresholdUpperValue?: number;
    evaluationCriteria?: IAlertEvaluationCriteria;
    notificationRecipients?: INotificationRecipient[];
    recipientsInternal?: string[];
    recipientsExternal?: string[];
    criteriaConditions?: IAlertCriteriaCondition[];
    notifyInApp?: boolean;
    currentValue?: number;
    triggerValue?: number;
    deviationPct?: number;
    description?: string;
    assignedUser?: ReferenceModel;
    cooldownUntil?: string;
    cooldownHours?: number;
    toleranceValue?: number;
    toleranceMode?: string;
    createdAt?: string;
    updatedAt?: string;
    active?: boolean;
}

/**
 * Alert card on the overview (`GET v1/alert`). Only what the card renders: add a field here and on the backend
 * `GetAlertOverviewModel` when the overview needs more.
 */
export interface IAlertOverviewModel {
    uuid: string;
    customer?: ReferenceModel;
    title: string;
    description?: string;
    severity?: ReferenceModel;
    status?: ReferenceModel;
    datapoint?: string;
    thresholdCondition?: ReferenceModel;
    thresholdUpperValue?: number;
    triggerValue?: number;
    toleranceValue?: number;
    toleranceMode?: string;
    currentValue?: number;
    deviationPct?: number;
    evaluationScopeMode?: AlertEvaluationScopeMode;
    triggeredScopeCount?: number;
    totalScopeCount?: number;
    assignedUser?: ReferenceModel;
    createdAt?: string;
    updatedAt?: string;
    active?: boolean;
}

/**
 * Alert detail page (`GET v1/alert/{uuid}/detail`). Only what the page itself renders; activity, value history,
 * tasks and comments are loaded separately. Add a field here and on the backend `GetAlertDetailModel` when the page
 * needs more.
 */
export interface IAlertDetailModel {
    uuid: string;
    customer?: ReferenceModel;
    title: string;
    severity?: ReferenceModel;
    status?: ReferenceModel;
    metricView?: ReferenceModel;
    dashboard?: ReferenceModel;
    datapoint?: string;
    runFrequency?: ReferenceModel;
    evaluationPeriod?: ReferenceModel;
    evaluationPeriodValue?: EvaluationPeriodValue;
    evaluationScopeMode?: AlertEvaluationScopeMode;
    evaluationScopes?: IAlertEvaluationScope[];
    thresholdCondition?: ReferenceModel;
    thresholdUpperValue?: number;
    triggerValue?: number;
    toleranceValue?: number;
    toleranceMode?: string;
    currentValue?: number;
    deviationPct?: number;
    criteriaConditions?: IAlertCriteriaCondition[];
    assignedUser?: ReferenceModel;
    lastRunTs?: string;
    nextRunTs?: string;
    cooldownUntil?: string;
    createdAt?: string;
}

export interface IAlertTask {
    uuid: string;
    title: string;
    description?: string | null;
    status?: string | null;
    priority?: string | null;
    createdAt?: string | null;
    createdBy?: ReferenceModel | null;
    assignedUser?: ReferenceModel | null;
    deadlineDate?: string | null;
}

export interface IAlertPayload {
    uuid?: string;
    customer?: ReferenceModel;
    title: string;
    type?: ReferenceModel;
    severity?: ReferenceModel;
    status?: ReferenceModel;
    metricView?: ReferenceModel;
    dashboard?: ReferenceModel;
    datapoint?: string;
    functionName?: string;
    datapointExpression?: string;
    datapointBuilderStateJson?: string;
    datapointConfigurationMode?: string;
    runFrequency?: ReferenceModel;
    evaluationStartOn?: string;
    evaluationPeriod?: ReferenceModel;
    evaluationPeriodValue?: EvaluationPeriodValue;
    evaluationScopeMode?: AlertEvaluationScopeMode;
    evaluationScopes?: IAlertEvaluationScope[];
    thresholdCondition?: ReferenceModel;
    thresholdUpperValue?: number;
    notificationRecipients?: INotificationRecipient[];
    recipientsInternal?: string[];
    recipientsExternal?: string[];
    criteriaConditions?: IAlertCriteriaCondition[];
    notifyInApp?: boolean;
    currentValue?: number;
    triggerValue?: number;
    deviationPct?: number;
    description?: string;
    assignedUser?: ReferenceModel;
    cooldownUntil?: string;
    cooldownHours?: number;
    toleranceValue?: number;
    toleranceMode?: string;
    active?: boolean;
    roles?: ReferenceModel[];
}

export interface IAlertEventModel {
    uuid: string;
    alertId: string;
    eventType: string;
    contentTypeIcon?: string;
    contentTypeDescription?: string;
    payload?: Record<string, any>;
    actorName?: string;
    createdBy?: string;
    createdAt?: string;
}

export interface IAlertCriteriaResultHistoryItem {
    uuid: string;
    evaluationCriteriaUuid?: string;
    evaluationScopeUuid?: string;
    evaluationScopeLabel?: string;
    executedAt?: string;
    value?: number;
    currentValue?: number;
    sampleSize?: number;
    resultStatus?: string;
    resultContext?: string;
}

export interface IAlertCriteriaPreviewPayload {
    alertUuid?: string;
    metricUuid?: string;
    metricViewUuid: string;
    datapoint: string;
    functionName?: string;
    datapointExpression?: string;
    datapointBuilderStateJson?: string;
    datapointConfigurationMode?: string;
    evaluationPeriod?: string;
    evaluationPeriodValue?: EvaluationPeriodValue;
    evaluationScopeMode?: AlertEvaluationScopeMode;
    evaluationScopes?: IAlertEvaluationScope[];
    conditions?: IProcessflowAutomaticCriteriaCondition[];
    thresholdConditionCode?: string;
    triggerValue?: number;
    thresholdUpperValue?: number;
    toleranceValue?: number;
    toleranceMode?: string;
}

export interface IAlertCriteriaPreviewResult {
    value?: number;
    sampleSize?: number;
    evaluationPeriod?: string;
    scopes?: IAlertEvaluationScopePreview[];
}

export interface IAlertCriteriaExecutionQueryPayload extends IAlertCriteriaPreviewPayload {
}

export interface IAlertCriteriaExecutionQueryResult {
    query?: string;
    previousPeriodQuery?: string;
}

export type AlertSeverity = "low" | "medium" | "high" | "critical";
export type AlertStatus = "open" | "in_progress" | "resolved";

const getReferenceCode = (reference?: ReferenceModel | null): string => (reference?.code || "").toLowerCase();

const normalizeAlert = <T extends { uuid?: string; assignedUser?: ReferenceModel }>(alert?: T | null): T | null => {
    if (!alert || !alert.uuid) {
        return null;
    }

    const assigneeUuid = alert.assignedUser?.uuid?.trim();

    return {
        ...alert,
        assignedUser: assigneeUuid ? alert.assignedUser : undefined,
    };
};

const dedupeAlertsByUuid = (alerts: IAlertOverviewModel[]): IAlertOverviewModel[] => {
    const seen = new Set<string>();
    const unique: IAlertOverviewModel[] = [];
    alerts.forEach((alert) => {
        if (!alert?.uuid || seen.has(alert.uuid)) {
            return;
        }
        seen.add(alert.uuid);
        unique.push(alert);
    });
    return unique;
};

export interface IHomeAlertItem {
    uuid: string;
    title: string;
    severity?: ReferenceModel;
    status?: ReferenceModel;
    metricView?: ReferenceModel;
    datapoint?: string;
    assignedUser?: ReferenceModel;
    createdAt?: string;
    updatedAt?: string;
    active?: boolean;
}

export interface IHomeAlert {
    triggeredCount: number;
    alerts: IHomeAlertItem[];
}

export class AlertService extends BaseService<IAlertOverviewModel, IAlertPayload> {
    public async fetchHome(params?: { customer?: string }): Promise<AxiosResponse<ApiResponse<IHomeAlert>>> {
        return api.get(`v1/${this.modelUrl()}/home`, { params });
    }

    public async fetchItems(params?: any): Promise<AxiosResponse<ApiResponse<Page<IAlertOverviewModel>>>> {
        const response = await api.get(`v1/${this.modelUrl()}`, { params });
        const page = response?.data?.data;

        if (page?.content && Array.isArray(page.content)) {
            page.content = dedupeAlertsByUuid(
                page.content
                .map((item: IAlertOverviewModel) => normalizeAlert(item))
                .filter(Boolean) as IAlertOverviewModel[]
            );
        }

        return response;
    }

    /** Full alert configuration for the create/edit form; pages use `fetchItems` / `fetchDetail`. */
    public async fetchItem(uuid: string): Promise<AxiosResponse<ApiResponse<IAlertModel>>> {
        const response = await api.get(`v1/${this.modelUrl()}/${uuid}`);
        response.data.data = normalizeAlert(response.data.data as IAlertModel) as any;
        return response;
    }

    public async fetchDetail(uuid: string): Promise<AxiosResponse<ApiResponse<IAlertDetailModel>>> {
        const response = await api.get(`v1/${this.modelUrl()}/${uuid}/detail`);
        response.data.data = normalizeAlert(response.data.data as IAlertDetailModel) as IAlertDetailModel;
        return response;
    }

    public async updateActive(alertId: string, active: boolean): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return await api.put(`v1/${this.modelUrl()}/${alertId}/active`, { active });
    }

    public async fetchOptionsByType(type: string): Promise<AxiosResponse<ApiResponse<ReferenceModel[]>>> {
        return await api.get("v1/generic-select-item/select-item/by-type", {
            params: { type }
        });
    }

    public async fetchEvents(alertId: string): Promise<AxiosResponse<ApiResponse<IAlertEventModel[]>>> {
        const response = await api.get(`v1/${this.modelUrl()}/${alertId}/event`);
        response.data.data = ((response.data.data as IAlertEventModel[]) || [])
            .map((event) => normalizeAlertEvent(event))
            .filter(Boolean) as IAlertEventModel[];
        return response;
    }

    public async fetchCriteriaHistory(
        alertId: string,
        limit = 30
    ): Promise<AxiosResponse<ApiResponse<IAlertCriteriaResultHistoryItem[]>>> {
        return await api.get(`v1/${this.modelUrl()}/${alertId}/criteria/history`, { params: { limit } });
    }

    public async addComment(alertId: string, text: string): Promise<AxiosResponse<ApiResponse<IAlertEventModel>>> {
        return await api.post(`v1/${this.modelUrl()}/${alertId}/comment`, { text });
    }

    public async deleteComment(alertId: string, commentUuid: string): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return await api.delete(`v1/${this.modelUrl()}/${alertId}/comment/${commentUuid}`);
    }

    public async addEmailEvent(alertId: string, to: string, subject: string, body?: string): Promise<AxiosResponse<ApiResponse<IAlertEventModel>>> {
        return await api.post(`v1/${this.modelUrl()}/${alertId}/email-event`, { to, subject, body });
    }

    public async updateStatus(alertId: string, statusUuid: string): Promise<AxiosResponse<ApiResponse<string>>> {
        return await api.post(`v1/${this.modelUrl()}/${alertId}/status`, { statusUuid });
    }

    public async resolveEvaluationScope(
        alertId: string,
        scopeUuid: string,
        payload: IAlertScopeResolvePayload,
    ): Promise<AxiosResponse<ApiResponse<IAlertDetailModel>>> {
        const response = await api.post(`v1/${this.modelUrl()}/${alertId}/scope/${scopeUuid}/resolve`, payload);
        response.data.data = normalizeAlert(response.data.data as IAlertDetailModel) as IAlertDetailModel;
        return response;
    }

    public async unresolveEvaluationScope(
        alertId: string,
        scopeUuid: string,
    ): Promise<AxiosResponse<ApiResponse<IAlertDetailModel>>> {
        const response = await api.post(`v1/${this.modelUrl()}/${alertId}/scope/${scopeUuid}/unresolve`);
        response.data.data = normalizeAlert(response.data.data as IAlertDetailModel) as IAlertDetailModel;
        return response;
    }

    public async assignUser(
        alertId: string,
        assignedUserUuid: string | null
    ): Promise<AxiosResponse<ApiResponse<IAlertDetailModel>>> {
        const response = await api.put(`v1/${this.modelUrl()}/${alertId}/assignee`, { assignedUserUuid });
        response.data.data = normalizeAlert(response.data.data as IAlertDetailModel) as IAlertDetailModel;
        return response;
    }

    public async triggerCriteriaRefresh(alertId: string): Promise<AxiosResponse<ApiResponse<any>>> {
        return await api.post(`v1/${this.modelUrl()}/${alertId}/criteria-refresh`);
    }

    public async submitCriteriaManualInput(
        alertId: string,
        payload: IAlertCriteriaManualInputPayload
    ): Promise<AxiosResponse<ApiResponse<any>>> {
        return await api.post(`v1/${this.modelUrl()}/${alertId}/criteria-manual-input`, payload);
    }

    public async previewCriteria(
        payload: IAlertCriteriaPreviewPayload
    ): Promise<AxiosResponse<ApiResponse<IAlertCriteriaPreviewResult>>> {
        return await api.post(`v1/${this.modelUrl()}/criteria-preview`, payload);
    }

    public async fetchCriteriaExecutionQuery(
        payload: IAlertCriteriaExecutionQueryPayload
    ): Promise<AxiosResponse<ApiResponse<IAlertCriteriaExecutionQueryResult>>> {
        return await api.post(`v1/${this.modelUrl()}/criteria-execution-query`, payload);
    }

    public getAlertSeverityCode(alert?: Pick<IAlertModel, "severity"> | null): AlertSeverity | "" {
        const code = getReferenceCode(alert?.severity);
        if (code === "critical" || code === "high" || code === "medium" || code === "low") {
            return code;
        }
        return "";
    }

    public getAlertStatusCode(alert?: Pick<IAlertModel, "status"> | null): AlertStatus | "" {
        const code = getReferenceCode(alert?.status);
        if (code === "open" || code === "in_progress" || code === "resolved") {
            return code;
        }
        return "";
    }

    public getReferenceCode(reference?: ReferenceModel | null): string {
        return getReferenceCode(reference);
    }

    public findStatusOptionByCode(options: ReferenceModel[], code: AlertStatus): ReferenceModel | undefined {
        return options.find((option) => getReferenceCode(option) === code);
    }

    public async fetchTasks(alertId: string): Promise<AxiosResponse<ApiResponse<IAlertTask[]>>> {
        return await api.get(`v1/${this.modelUrl()}/${alertId}/task`);
    }

    public async createTask(
        alertId: string,
        payload: {
            title: string;
            description?: string;
            priority?: string;
            assignedUserUuid?: string;
        },
    ): Promise<AxiosResponse<ApiResponse<IAlertTask>>> {
        return await api.post(`v1/${this.modelUrl()}/${alertId}/task`, payload);
    }

    public async updateTask(
        taskUuid: string,
        payload: Partial<Pick<IAlertTask, "title" | "description" | "status" | "priority">>,
    ): Promise<AxiosResponse<ApiResponse<IAlertTask>>> {
        return await api.put(`v1/${this.modelUrl()}/task/${taskUuid}`, payload);
    }

    public async assignTaskUser(taskUuid: string, assignedUserUuid: string | null): Promise<AxiosResponse<ApiResponse<IAlertTask>>> {
        return await api.put(`v1/${this.modelUrl()}/task/${taskUuid}/assignee`, { assignedUserUuid });
    }

    protected modelUrl(): string {
        return "alert";
    }
}
